import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { supplierKeyForUser } from '../../roles';
import { laserEngravingAccessForUser } from '../../utils/laserEngravingAccess';
import { canUseChangeControl } from './changes/changeControl';
import { peAccessForUser } from './pe/peActions';

const DOMAINS = [
  { key: 'sample-investigations', title: 'Sample investigations', subtitle: 'First samples, part revisions, photographic findings and supplier performance',
    route: '/quality/sample-investigations', icon: '🔎', color: '#60a5fa', stats: null },
  { key: 'test-tools', title: 'Tools and software', subtitle: 'Published versions, release history and scoped production approvals',
    route: '/quality/test-tools', icon: '💻', color: '#34d399', stats: null },
  { key: 'pe-task-follow-up', title: 'PE Task Follow-Up', subtitle: 'Weekly production actions, owners, blockers and verified closure',
    route: '/quality/pe-task-follow-up', icon: '📍', color: '#fb923c', stats: null },
  { key: 'pe-production-improvement', title: 'PE Production Improvement', subtitle: 'Biweekly line improvements, baseline, targets and measured results',
    route: '/quality/pe-production-improvement', icon: '↗', color: '#60a5fa', stats: null },
  {
    key: 'change-control', title: 'Change Control',
    subtitle: 'ECO, PCR and PDR drafts, preserved originals, revisions and NI decisions',
    route: '/quality/change-control', icon: '↔', color: '#46BB89', stats: null,
  },
  {
    key: 'laser-engraving',
    title: 'Laser Engraving',
    subtitle: 'UID labels, service tags and early production records',
    route: '/quality/laser-engraving',
    icon: '🏷️',
    color: '#46BB89',
    stats: null,
  },
  {
    key: 'documents',
    title: 'Quality Documents',
    subtitle: 'Procedures, work instructions, inspection criteria',
    route: '/quality/documents',
    icon: '📄',
    color: '#46BB89',
    stats: null,
  },
  {
    key: 'process',
    title: 'Process Engineering',
    subtitle: 'Production flow design, gate logic, station dependencies',
    route: '/quality/process',
    icon: '🏭',
    color: '#60a5fa',
    stats: null,
  },
  {
    key: 'suppliers',
    title: 'Supplier Matrix',
    subtitle: 'Suppliers, capabilities & ratings, contacts, terms',
    route: '/quality/suppliers',
    icon: '🏢',
    color: '#f59e0b',
    stats: null,
  },
  {
    key: 'jigs',
    title: 'Jig Library',
    subtitle: 'Fixture templates, dimensions, linked procedures',
    route: '/quality/jigs',
    icon: '🔩',
    color: '#a78bfa',
    stats: null,
  },
  {
    key: 'equipment',
    title: 'Equipment Registry',
    subtitle: 'Test benches, instruments, calibration tracking',
    route: '/quality/equipment',
    icon: '⚙️',
    color: '#f59e0b',
    stats: null,
  },
  {
    key: 'test-library',
    title: 'Test Library',
    subtitle: 'Firmware flash, functional tests, burn-in procedures',
    route: '/quality/test-library',
    icon: '🧪',
    color: '#34d399',
    stats: null,
  },
  {
    key: 'assembly-instructions',
    title: 'Assembly Instructions',
    subtitle: 'Step-by-step build guides, linked to the process flow',
    route: '/quality/assembly-instructions',
    icon: '🛠️',
    color: '#2dd4bf',
    stats: null,
  },
  {
    key: 'boms',
    title: 'Bills of Materials',
    subtitle: 'Component lists per product family, linked to process-flow stations',
    route: '/quality/boms',
    icon: '🧾',
    color: '#f472b6',
    stats: null,
  },
  {
    key: 'standards',
    title: 'Compliance Standards',
    subtitle: 'IEC / IPC / ISO standards NI builds & tests to — the QMS standards catalogue',
    route: '/quality/standards',
    icon: '📐',
    color: '#818cf8',
    stats: null,
  },
  {
    key: 'notes',
    title: 'Resource Notes',
    subtitle: 'Notes and actions attached to processes, equipment, jigs and tests',
    route: '/quality/notes',
    icon: '📌',
    color: '#fb923c',
    stats: null,
  },
  {
    key: 'calibration',
    title: 'Calibration',
    subtitle: 'Overdue alerts, service history, upcoming schedule',
    route: '/quality/calibration',
    icon: '📅',
    color: '#38bdf8',
    stats: null,
  },
  {
    key: 'analytics',
    title: 'QMS Analytics',
    subtitle: 'First pass yield, test pass rates, defect trends, document lifecycle',
    route: '/quality/analytics',
    icon: '📊',
    color: '#38bdf8',
    stats: null,
  },
  {
    key: 'execution',
    title: 'Quality Execution',
    subtitle: 'Work orders, production sign-off, test execution',
    route: '/quality/execution',
    icon: '✅',
    color: '#e879f9',
    stats: null,
  },
];

export default function QualityHubLanding() {
  const navigate = useNavigate();
  const { user } = useUser();
  // Shared, backend-mirroring helper — handles arrays, all metadata sources,
  // test- variants and the hk-ringer/hk_ringer separator difference.
  const isSupplier = !!supplierKeyForUser(user);

  // Suppliers see the collaborative libraries they can contribute to (draft +
  // submit for review); NI retains release control and the NI-only domains.
  const laserAccess = laserEngravingAccessForUser(user);
  const supplierDomains = ['documents', 'process', 'jigs', 'equipment', 'test-library', 'test-tools', 'assembly-instructions', 'boms', 'standards', 'notes', 'suppliers'];
  const visibleDomains = DOMAINS.filter(domain => domain.key.startsWith('pe-') ? peAccessForUser(user).allowed : ['change-control', 'sample-investigations'].includes(domain.key) ? canUseChangeControl(user) : domain.key === 'laser-engraving'
    ? laserAccess.allowed
    : !isSupplier || supplierDomains.includes(domain.key));

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: '-0.01em' }}>
          Product QMS
        </h1>
        <p style={{ margin: '6px 0 0', color: 'var(--ni-text-muted)', fontSize: 14 }}>
          Quality management, process engineering, and production tooling
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 16,
      }}>
        {visibleDomains.map(domain => (
          <DomainCard key={domain.key} domain={domain} onClick={() => navigate(domain.route)} />
        ))}
      </div>
    </div>
  );
}

function DomainCard({ domain, onClick }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.05)' : 'var(--ni-panel)',
        border: `1px solid ${hovered ? domain.color + '55' : 'var(--ni-border-soft)'}`,
        borderRadius: 12,
        padding: '20px 24px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 28 }}>{domain.icon}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ni-text)' }}>
            {domain.title}
          </div>
        </div>
      </div>
      <p style={{ margin: 0, color: 'var(--ni-text-muted)', fontSize: 13, lineHeight: 1.5 }}>
        {domain.subtitle}
      </p>
      <div style={{
        marginTop: 4,
        fontSize: 12, fontWeight: 600, color: domain.color,
        letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>
        Open →
      </div>
    </div>
  );
}
